import {
    createEntityAdapter,
    createSelector,
    createSlice,
    EntityState,
  } from "@reduxjs/toolkit";
  import { RootState } from "../../../app/store";
  import { DisbursementEntity } from "../../../api/disbursements";
  import { RequestStatus } from "../../legalTechnicalAnalysis/Logic/assetsSlice";
  import {
    fetchDisbursements,
    upsertDisbursements,
  } from "./disbursementsThunk";

export const disbursementsAdapter = createEntityAdapter<
    DisbursementEntity,
    string
  >({
    selectId: (disbursement) => disbursement.disbursement_id,
});

export interface DisbursementsState {
    disbursements: EntityState<DisbursementEntity, string>;
    status: RequestStatus;
    error?: string | null;
    caseId?: string;
}

const initialState: DisbursementsState = {
    disbursements: disbursementsAdapter.getInitialState(),
    status: "idle",
    error: null,
    caseId: undefined,
};

const disbursementsSlice = createSlice({
    name: "disbursements",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetchDisbursements.pending, (state) => {
          state.status = "loading";
          state.error = null;
          disbursementsAdapter.removeAll(state.disbursements);
        })
        .addCase(fetchDisbursements.fulfilled, (state, action) => {
          state.status = "success";
          state.caseId = action.meta.arg;
          state.error = null;

          disbursementsAdapter.setAll(
            state.disbursements,
            action.payload.disbursements,
          );
        })
        .addCase(fetchDisbursements.rejected, (state) => {
          state.status = "failed";
          state.error = "Failed to fetch disbursements";
        })
        .addCase(upsertDisbursements.fulfilled, (state, action) => {
          disbursementsAdapter.upsertMany(
            state.disbursements,
            action.meta.arg.request.disbursements,
          );
        });
    },
});

export const disbursementsReducer = disbursementsSlice.reducer;

const disbursementsSelectors =
    disbursementsAdapter.getSelectors<RootState>(
      (state) => state.disbursements.disbursements,
);

export const {
    selectAll: selectAllDisbursements,
    selectById: selectDisbursementById,
    selectEntities: selectDisbursementEntities,
    selectIds: selectDisbursementIds,
    selectTotal: selectDisbursementCount,
} = disbursementsSelectors;

export const selectDisbursementsStatus = (state: RootState) =>
    state.disbursements.status;

export const selectDisbursementsError = (state: RootState) =>
    state.disbursements.error;

export const selectDisbursementsCaseId = (state: RootState) =>
    state.disbursements.caseId;

export const selectDisbursementsSortedByDate = createSelector(
    [selectAllDisbursements],
    (rows) =>
      [...rows].sort((a, b) =>
        a.disbursement_date.localeCompare(b.disbursement_date),
      ),
);

export const selectDisbursementTableRows = createSelector(
    [selectAllDisbursements],
    (disbursements) => disbursements,
);